import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import "./iris.css";
import LINK_PREVIEWS from "./link_previews.json";
import SETS_MEDIA from "./sets_media.json";

// Precomputed, VERBATIM Open Graph metadata for the bounded set of URLs Iris
// cites (it's RAG over opal_public_content, so the link set is finite). Built by
// IRIS/kb/og_previews.py. Known URL -> rich unfurl card; unknown URL keeps the
// styled inline anchor (the fallback). No hand-typed blurbs: see no-content-washing.
const PREVIEWS = LINK_PREVIEWS.previews || {};
const MAX_PREVIEWS = 3;

// URL -> Opal set/aftermovie metadata (artwork, title, artist, plays). Built by
// IRIS/kb/sets_media_json.py from the opal_public_sets snapshot, so a card's data
// never drifts from the index. When Iris hands a known SoundCloud/YouTube link, we
// render a facade (thumbnail + play); one click swaps in the real player iframe, so
// no third-party tracker loads until the visitor asks to listen/watch.
const MEDIA = SETS_MEDIA.media || {};
const MAX_MEDIA = 3; // cogload ceiling: never stack more than 3 players in one answer

// Public, client-side config. The search-only key is scoped to opal_public* and
// also authorizes the Agent Studio completions endpoint from an allowed origin.
const APP_ID = process.env.REACT_APP_ALGOLIA_APP_ID || "TDNMRH8LS3";
const SEARCH_KEY = process.env.REACT_APP_ALGOLIA_SEARCH_KEY;
const AGENT_ID =
  process.env.REACT_APP_IRIS_AGENT_ID || "6a7da8c7-bed6-44bc-81e7-b1002dcd3c78";
const COMPLETIONS = `https://${APP_ID}.algolia.net/agent-studio/1/agents/${AGENT_ID}/completions?compatibilityMode=ai-sdk-5`;

// Anonymous-but-persistent identity. We pass it as the completion `id` so a returning
// visitor keeps ONE continuous conversation thread (continuity + clean analytics),
// no server needed. This is the "for now": cross-session MEMORY and authenticated-user
// analytics need the secure-user-token (a server-signed JWT), which is the next step.
// Stored once in localStorage; reused later as the JWT `sub` when logins land. Degrades
// gracefully to no-id if localStorage is unavailable (private mode / SSR).
let _irisUid;
function irisUserToken() {
  if (_irisUid !== undefined) return _irisUid;
  _irisUid = null;
  if (typeof window !== "undefined") {
    try {
      let t = window.localStorage.getItem("iris_uid");
      if (!t) {
        const rnd =
          window.crypto && window.crypto.randomUUID
            ? window.crypto.randomUUID()
            : Math.random().toString(36).slice(2) + Date.now().toString(36);
        t = `anon-${rnd}`;
        window.localStorage.setItem("iris_uid", t);
      }
      _irisUid = t;
    } catch {
      _irisUid = null; // localStorage blocked -> stateless, still works
    }
  }
  return _irisUid;
}

// Secure-user-token (server-signed JWT) for authenticated memory + analytics (#36).
// OFF today: with no endpoint configured we send only the body `id` (anonymous
// continuity, current behaviour). Point REACT_APP_IRIS_TOKEN_ENDPOINT at the
// api.nech.pl signer (see IRIS/auth/) to flip it on: the server holds the secret,
// the browser only fetches a short-lived token, cached until just before it expires.
// The anon `iris_uid` rides as the JWT `sub`, so continuity carries into the authed
// world. Until the dashboard `kid` lands the endpoint 503s and we degrade silently.
const TOKEN_ENDPOINT = process.env.REACT_APP_IRIS_TOKEN_ENDPOINT;
let _sut = null; // { token, validUntil }
async function secureUserToken() {
  if (!TOKEN_ENDPOINT) return null;
  const now = Math.floor(Date.now() / 1000);
  if (_sut && _sut.validUntil - 60 > now) return _sut.token;
  try {
    const res = await fetch(TOKEN_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sub: irisUserToken() }),
    });
    if (!res.ok) return null; // 503 until the kid lands -> fall back to body id
    const data = await res.json();
    if (data?.token) { _sut = data; return data.token; }
  } catch { /* network -> degrade to anonymous */ }
  return null;
}

// --- memory controls (#57) -------------------------------------------------
// Agent Studio memory binds to the secure-user-token `sub` (= our iris_uid) and
// auto-stores from completions. So "memory" is exactly: do we attach the token?
//   ON  -> token attached, conversation id = the persistent iris_uid (recall across sessions)
//   OFF -> no token,        conversation id = an ephemeral per-session id (no cross-session trace)
// The platform exposes no list / delete / used-memory surface (docs/algolia.md §14), so the
// only honest "forget" is rotating the uid (a fresh sub orphans the old memories; they're
// never recalled and expire at retention), and "what do you remember about me" is a
// meta-query whose prose answer we render, not a structured readout.
let _memoryEnabled = true;          // mirror of the localStorage pref, synced by useMemory()
let _sessionId = null;              // ephemeral conversation id used while memory is OFF

function ephemeralId() {
  if (_sessionId) return _sessionId;
  const rnd =
    typeof window !== "undefined" && window.crypto && window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
  _sessionId = `eph-${rnd}`;
  return _sessionId;
}

// The conversation id we send: a stable per-visitor thread when remembering, a throwaway
// per-session id when not (multi-turn still threads, feedback still persists, but nothing
// ties back to the durable identity across sessions).
// CRUCIAL: when a token is attached the conversation id MUST differ from the token `sub`
// (== the uid). Agent Studio 400s "User token does not match for conversation with id"
// when they collide. Memory binds to the token sub, not this id, so we just prefix it.
function conversationId() {
  if (!_memoryEnabled) return ephemeralId();
  const uid = irisUserToken();
  return uid ? `conv-${uid}` : null;
}

function applyMemoryEnabled(on) {
  _memoryEnabled = on;
  try { window.localStorage.setItem("iris_memory", on ? "on" : "off"); } catch { /* private mode */ }
}

// Forget = rotate the anonymous identity. The new sub means Agent Studio recalls nothing
// of the old one; the orphaned memories expire at the retention window. This is the whole
// of "erase my memories" the platform allows today (no per-user delete endpoint exists).
function rotateIris() {
  let t = null;
  try {
    const rnd = window.crypto && window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    t = `anon-${rnd}`;
    window.localStorage.setItem("iris_uid", t);
  } catch { t = null; }
  _irisUid = t;        // refresh the cached identity (null if storage is blocked)
  _sut = null;         // drop the cached token: it signed the OLD sub
  _sessionId = null;   // and any ephemeral thread
}

// Standalone tool (opal.nech.pl/iris) has no guildes page to go back to.
const STANDALONE = process.env.REACT_APP_IRIS_STANDALONE === "1";

// Gentle dark/light: default follows prefers-color-scheme, a toggle overrides and
// persists. We resolve to a concrete theme in JS and stamp data-theme on the page
// root, so the CSS needs a single [data-theme="dark"] block.
function useTheme() {
  const mq = () =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;
  const [pref, setPref] = useState(() => {
    try { return window.localStorage.getItem("iris_theme"); } catch { return null; }
  });
  const [sysDark, setSysDark] = useState(() => !!mq()?.matches);
  useEffect(() => {
    const m = mq();
    if (!m) return;
    const on = (e) => setSysDark(e.matches);
    m.addEventListener?.("change", on);
    return () => m.removeEventListener?.("change", on);
  }, []);
  const resolved = pref || (sysDark ? "dark" : "light");
  const toggle = useCallback(() => {
    setPref((p) => {
      const next = (p || (sysDark ? "dark" : "light")) === "dark" ? "light" : "dark";
      try { window.localStorage.setItem("iris_theme", next); } catch { /* private mode */ }
      return next;
    });
  }, [sysDark]);
  return { resolved, toggle };
}

// Feedback capture. A scoped write key (addObject on opal_feedback) lands
// lightweight records, triaged by hand later, NEVER auto-promoted to opal_content.
// The key is a credential: it comes from the env, never an in-source default.
const FEEDBACK_KEY = process.env.REACT_APP_ALGOLIA_FEEDBACK_KEY;
const FEEDBACK_URL = `https://${APP_ID}.algolia.net/1/indexes/opal_feedback`;

async function submitFeedback(record) {
  if (!FEEDBACK_KEY) throw new Error("clé feedback manquante");
  const res = await fetch(FEEDBACK_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Algolia-Application-Id": APP_ID,
      "X-Algolia-API-Key": FEEDBACK_KEY,
    },
    body: JSON.stringify({
      app: "iris",
      at: new Date().toISOString(),
      path: typeof window !== "undefined" ? window.location.pathname : undefined,
      ...record,
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// Mirror a thumb to the agent's NATIVE per-message feedback so the Agent Studio
// dashboard metric populates (opal_feedback stays the richer triage surface).
// Best-effort telemetry: one vote per message server-side (dupes 400), so we
// swallow all errors. Needs the server messageId captured from the stream (#38).
// The schema also carries optional `notes` (<=1000) + `tags` (<=10): when the
// visitor writes a correction we send it through here too, so the dashboard sees
// WHY, not just the thumb (#41). That's why the vote fires once, at feedback
// submit/cancel time, never on the bare click (a second call would 400 as a dupe).
function voteFeedback(messageId, vote, extra) {
  if (!messageId || !SEARCH_KEY) return;
  const body = { messageId, agentId: AGENT_ID, vote };
  const notes = extra?.notes?.trim();
  if (notes) body.notes = notes.slice(0, 1000);
  if (extra?.tags?.length) body.tags = extra.tags.slice(0, 10);
  fetch(`https://${APP_ID}.algolia.net/agent-studio/1/feedback`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Algolia-Application-Id": APP_ID,
      "X-Algolia-API-Key": SEARCH_KEY,
    },
    body: JSON.stringify(body),
  }).catch(() => {});
}

// Feedback mode reuses the composer; each kind just reframes the same input.
// optional=true means a bare sentiment is enough (a thumb with no words).
const FEEDBACK_UI = {
  up: {
    icon: "👍",
    title: "Qu'est-ce qui t'a plu ? Ça m'aide à viser juste",
    placeholder: "Ce qui était top (Entrée pour envoyer)…",
    optional: true,
  },
  down: {
    icon: "👎",
    title: "Qu'est-ce qui cloche ? Corrige-moi, j'apprends 🌱",
    placeholder: "Ce qui est faux, et ce qui serait juste…",
    optional: true,
  },
  idea: {
    icon: "💡",
    title: "Une idée, un bug, un mot pour l'équipe Opal ?",
    placeholder: "Balance, l'équipe lira et triera derrière…",
    optional: false,
  },
};

// type -> badge. Emoji + label ALWAYS present (never color-as-only-signal).
// Colours are saturated mid-tones so white badge text stays readable.
const BADGES = {
  infos: { emoji: "🌲", label: "Infos", color: "oklch(0.55 0.13 150)" },
  faq: { emoji: "❓", label: "FAQ", color: "oklch(0.58 0.13 230)" },
  checklist: { emoji: "🎒", label: "Packing", color: "oklch(0.6 0.13 175)" },
  links: { emoji: "🔗", label: "Liens", color: "oklch(0.55 0.15 300)" },
  guilde: { emoji: "🪵", label: "Guilde", color: "oklch(0.5 0.12 150)" },
  valeur: { emoji: "💎", label: "Valeur", color: "oklch(0.62 0.16 350)" },
};

const SUGGESTIONS = [
  "Ça ouvre quand ?",
  "Qu'est-ce que j'amène ?",
  "C'est vraiment sans argent ?",
  "C'est quoi les valeurs d'Opal ?",
];

// Persistent exit-to-action CTAs. Iris is here to push you OUT of the chat toward
// doing things. Maintained/refined per les bulles.
const FOOTER_LINKS = [
  { label: "Site officiel", href: "https://opal-festival.com" },
  { label: "Prends ton shift", href: "https://opal-festival.com/noe" },
  { label: "Discord", href: "https://discord.gg/vXbnzUne34" },
  { label: "Mon histoire", href: "https://fr.wikipedia.org/wiki/Iris_(mythologie)" },
];

// Context-derived exit CTAs: Iris pushes you OUT to act, not into another question.
// Matched against the answer text; first 2 distinct win, Site officiel as default.
// Opal is participative AND sans argent, so every CTA is a "participate" action,
// never "buy a ticket". See [[feedback-iris-brand]] (action-engine, not a chatbot).
const CTAS = [
  // Packing first: when the answer is about what to bring, the complete list is
  // the single most useful exit. Mirrors the agent's packing instruction.
  { re: /amener|emporter|packing|valise|sac \w|bagage|duvet|tente|affaires|pr[ée]parer? son sac|que prendre/i,
    emoji: "🎒", label: "Voir la liste complète",
    href: "https://opalcommunity.notion.site/Festival-packing-list-72610d29cd5a404bbf4afc5bdb49a69a" },
  { re: /shift|b[ée]n[ée]vol|no[ée]\b|aider|coup de main|[ée]quipe|orga|impliqu/i,
    emoji: "🤝", label: "Prends ton shift", href: "https://opal-festival.com/noe" },
  { re: /\banim|atelier|proposer|partager? un savoir|workshop/i,
    emoji: "✨", label: "Propose une animation", href: "https://www.opal-festival.com/anim" },
  { re: /artiste|musiq|concert|sc[èe]ne|line.?up|programmation|jouer|dj\b|groupe de musique/i,
    emoji: "🎤", label: "Candidate à la scène", href: "https://www.opal-festival.com/artiste" },
  { re: /discord|communaut|rencontr|rejoin|groupe|discut|chat\b/i,
    emoji: "💬", label: "Rejoins le Discord", href: "https://discord.gg/vXbnzUne34" },
];
const CTA_DEFAULT = { emoji: "🌲", label: "Site officiel", href: "https://opal-festival.com" };

// Normalize an href for comparison (drop scheme/www/trailing slash, lowercase),
// so a link Iris wrote as "opal-festival.com/artiste" matches a CTA's canonical
// "https://www.opal-festival.com/artiste".
const normHref = (u) =>
  (u || "").replace(/^https?:\/\/(www\.)?/i, "").replace(/\/+$/, "").toLowerCase();

// Which exit-CTAs to surface under an answer. A festivalier flagged (#44) that the
// highlight pills could contradict the links Iris actually wrote inline (music
// answer cited /artiste + Discord, but the regex surfaced "shift" + "anim"). So:
// first prefer CTAs whose destination Iris CITED in the answer (never contradictory);
// only when she cited no actionable link do we fall back to the contextual regex.
function exitCtas(text) {
  const t = text || "";
  const cited = new Set();
  const re = /\[[^\]]+\]\(([^)\s]+)\)|(https?:\/\/[^\s)]+)/g;
  let m;
  while ((m = re.exec(t))) {
    cited.add(normHref((m[1] || m[2] || "").replace(/[.,;:!?]+$/, "")));
  }
  const picks = [];
  // 1) CTAs Iris actually linked to -> confirmed, aligned with the prose
  for (const c of CTAS) {
    if (cited.has(normHref(c.href)) && !picks.some((p) => p.href === c.href)) picks.push(c);
    if (picks.length >= 2) break;
  }
  // 2) only if she cited nothing actionable, fall back to context (regex on the text)
  if (picks.length === 0) {
    for (const c of CTAS) {
      if (c.re.test(t) && !picks.some((p) => p.href === c.href)) picks.push(c);
      if (picks.length >= 2) break;
    }
  }
  return picks.length ? picks : [CTA_DEFAULT];
}

// The official "Tout savoir" Notion: where Iris sends people to double-check.
const NOTION_URL =
  "https://opalcommunity.notion.site/Tout-ce-qu-il-faut-savoir-sur-le-festival-Opal-2026-6e5d8bfbaafb46938bda2f50368082af";

// Raw search-hit cards are too noisy (they dump every hit verbatim). Disabled until
// the agent curates what to surface via the algolia_display_results tool (see tasks).
const SHOW_RECORDS = false;

// Extract knowledge records from a tool-output payload, defensively (the search
// tool returns hits; shape can be {hits:[...]} or a raw array).
function recordsFromToolOutput(output) {
  if (!output) return [];
  const hits = Array.isArray(output)
    ? output
    : output.hits || output.results?.[0]?.hits || output.records || [];
  return (Array.isArray(hits) ? hits : [])
    .filter((h) => h && (h.title || h.content))
    .slice(0, 4)
    .map((h) => ({
      type: h.type,
      title: h.title,
      content: h.content,
      url: h.url,
      join_url: h.join_url,
      source: h.source,
      color: h.color,
    }));
}

// --- minimal markdown for Iris's answers (bold, links, lists, paragraphs) ---
// The agent emits simple markdown; a tiny CRA-safe renderer beats pulling a dep.
function inline(text, kp) {
  const out = [];
  const re = /\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s)]+)/g;
  let last = 0, m, k = 0;
  const bold = (s, bk) =>
    s.split(/\*\*([^*]+)\*\*/g).map((p, i) =>
      i % 2 ? <strong key={bk + "b" + i}>{p}</strong> : p
    );
  // a bare-URL label (Iris sometimes writes [https://…/x](https://…/x)) reads
  // cleaner stripped of scheme/www/trailing slash; the href stays intact.
  const pretty = (s) =>
    /^https?:\/\//i.test(s) ? s.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/+$/, "") : s;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(...bold(text.slice(last, m.index), kp + k));
    const href = m[2] || m[3];
    const label = pretty(m[1] || m[3]);
    out.push(
      <a key={kp + "a" + k} href={href} target="_blank" rel="noreferrer">{label}</a>
    );
    last = re.lastIndex; k++;
  }
  if (last < text.length) out.push(...bold(text.slice(last), kp + "z"));
  return out;
}
function Rich({ text }) {
  const lines = (text || "").split("\n");
  const blocks = [];
  let list = null;
  const isRow = (s) => /^\s*\|.*\|\s*$/.test(s);
  const isSep = (s) => /-/.test(s) && /^\s*\|?[\s:|-]{3,}\|?\s*$/.test(s);
  const cells = (s) => s.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i];
    // markdown table: header row + separator + body rows (the markdownTable shape)
    if (isRow(ln) && i + 1 < lines.length && isSep(lines[i + 1])) {
      const head = cells(ln);
      const rows = [];
      let j = i + 2;
      while (j < lines.length && isRow(lines[j])) { rows.push(cells(lines[j])); j++; }
      blocks.push({ table: { head, rows } });
      list = null; i = j - 1;
      continue;
    }
    const li = ln.match(/^\s*[-*]\s+(.*)/);
    const oli = ln.match(/^\s*\d+\.\s+(.*)/);
    if (li || oli) {
      if (!list || list.ordered !== !!oli) { list = { ordered: !!oli, items: [] }; blocks.push(list); }
      list.items.push(li ? li[1] : oli[1]);
    } else {
      list = null;
      if (ln.trim()) blocks.push({ p: ln });
    }
  }
  return (
    <>
      {blocks.map((b, i) =>
        b.table ? (
          <table className="iris-md-table" key={i}>
            <thead><tr>{b.table.head.map((c, j) => <th key={j}>{inline(c, `${i}-h${j}-`)}</th>)}</tr></thead>
            <tbody>{b.table.rows.map((r, j) => (
              <tr key={j}>{r.map((c, k) => <td key={k}>{inline(c, `${i}-${j}-${k}-`)}</td>)}</tr>
            ))}</tbody>
          </table>
        ) : b.items ? (
          b.ordered ? (
            <ol key={i}>{b.items.map((it, j) => <li key={j}>{inline(it, `${i}-${j}-`)}</li>)}</ol>
          ) : (
            <ul key={i}>{b.items.map((it, j) => <li key={j}>{inline(it, `${i}-${j}-`)}</li>)}</ul>
          )
        ) : (
          <p key={i}>{inline(b.p, `${i}-`)}</p>
        )
      )}
    </>
  );
}

// --- link unfurling: distinct URLs in a message that we have OG data for -----
// Slack/Discord-style: the inline link stays in the prose; a rich card unfurls
// below for URLs in PREVIEWS. Unknown URLs get no card (the anchor is enough).
function previewsIn(text) {
  const re = /\[[^\]]+\]\(([^)\s]+)\)|(https?:\/\/[^\s)]+)/g;
  const out = [];
  const seen = new Set();
  let m;
  while ((m = re.exec(text || ""))) {
    const href = (m[1] || m[2] || "").replace(/[.,;:!?]+$/, "");
    const data = PREVIEWS[href] ||
      PREVIEWS[href.endsWith("/") ? href.slice(0, -1) : href + "/"];
    if (data && !seen.has(data.url)) { seen.add(data.url); out.push(data); }
    if (out.length >= MAX_PREVIEWS) break;
  }
  return out;
}

function LinkPreview({ data }) {
  const [imgOk, setImgOk] = useState(!!data.image);
  const [favOk, setFavOk] = useState(true);
  return (
    <a className="iris-lp" href={data.url} target="_blank" rel="noreferrer"
       title={data.title}>
      {imgOk && (
        <img className="iris-lp__img" src={data.image} alt="" loading="lazy"
             onError={() => setImgOk(false)} />
      )}
      <span className="iris-lp__txt">
        <span className="iris-lp__dom">
          {favOk ? (
            <img className="iris-lp__fav" src={`https://${data.domain}/favicon.ico`}
                 alt="" loading="lazy" onError={() => setFavOk(false)} />
          ) : (
            <span className="iris-lp__fav iris-lp__fav--leaf" aria-hidden="true">🌿</span>
          )}
          {data.domain}
        </span>
        <span className="iris-lp__title">{data.title}</span>
        {data.description && <span className="iris-lp__desc">{data.description}</span>}
      </span>
    </a>
  );
}

// --- media cards: SoundCloud/YouTube sets & aftermovies Iris hands out -------
// Facade pattern: a known media URL renders a poster (artwork + play); the heavy
// third-party iframe loads only on click, so no SoundCloud/YouTube tracker runs
// until the visitor chooses to play. Metadata comes from sets_media.json, not the
// link text, so the card is rich (artist, edition, plays), never fabricated.
function mediaIn(text) {
  const re = /\[[^\]]+\]\(([^)\s]+)\)|(https?:\/\/[^\s)]+)/g;
  const out = [];
  const seen = new Set();
  let m;
  while ((m = re.exec(text || ""))) {
    const href = (m[1] || m[2] || "").replace(/[.,;:!?]+$/, "");
    const d = MEDIA[href];
    if (d && !seen.has(d.url)) { seen.add(d.url); out.push(d); }
    if (out.length >= MAX_MEDIA) break;
  }
  return out;
}

const KIND_LABEL = { live_set: "Set", selects: "Sélection", aftermovie: "Aftermovie", trailer: "Trailer" };

function ytId(url) {
  const m = (url || "").match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
  return m ? m[1] : null;
}
function embedSrc(d) {
  if (d.host === "youtube") {
    const id = ytId(d.url);
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null;
  }
  if (d.host === "soundcloud") {
    return `https://w.soundcloud.com/player/?url=${encodeURIComponent(d.url)}` +
      "&auto_play=true&hide_related=true&show_comments=false&visual=false&color=%23ff7eb6";
  }
  return null;
}

function MediaCard({ data }) {
  const [playing, setPlaying] = useState(false);
  const [imgOk, setImgOk] = useState(!!data.image);
  const hostLabel = data.host === "youtube" ? "YouTube" : "SoundCloud";
  const kindLabel = KIND_LABEL[data.kind] || "Média";
  const sub = [data.artist, data.edition ? `Opal ${data.edition}` : null].filter(Boolean).join(" · ");
  const meta = [
    data.duration_min ? `${data.duration_min} min` : null,
    data.plays ? `${data.plays} écoutes` : null,
  ].filter(Boolean).join(" · ");

  if (playing) {
    const src = embedSrc(data);
    if (!src) return null;
    return (
      <div className={`iris-media iris-media--playing iris-media--${data.host}`}>
        <iframe className="iris-media__frame" src={src} title={data.title}
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy" />
      </div>
    );
  }
  return (
    <button type="button" className={`iris-media iris-media--${data.host}`}
            onClick={() => setPlaying(true)}
            aria-label={`Lire ${data.title} sur ${hostLabel}`}>
      {imgOk && (
        <img className="iris-media__thumb" src={data.image} alt="" loading="lazy"
             onError={() => setImgOk(false)} />
      )}
      <span className="iris-media__scrim" aria-hidden="true" />
      <span className="iris-media__play" aria-hidden="true">▶</span>
      <span className="iris-media__txt">
        <span className="iris-media__badge">{kindLabel} · {hostLabel}</span>
        <span className="iris-media__title">{data.title}</span>
        {(sub || meta) && (
          <span className="iris-media__sub">{[sub, meta].filter(Boolean).join("   ·   ")}</span>
        )}
      </span>
    </button>
  );
}

// --- thin Agent Studio streaming client (ai-sdk-5 data-stream protocol) ------
// No UI kit: we POST the conversation and parse the SSE ourselves so we own the
// generative UI (surfacing records in-thread).
function useIris() {
  const [messages, setMessages] = useState([]); // {id, role, text, records}
  const [busy, setBusy] = useState(false);
  const idRef = useRef(0);

  const send = useCallback(
    async (text) => {
      const clean = text.trim();
      if (!clean || busy) return;
      const userMsg = { id: ++idRef.current, role: "user", text: clean, records: [] };
      const botId = ++idRef.current;
      const history = [...messages, userMsg];
      setMessages([...history, { id: botId, role: "assistant", text: "", records: [], suggestions: [], actions: [] }]);
      setBusy(true);

      const patch = (fn) =>
        setMessages((prev) => prev.map((m) => (m.id === botId ? fn(m) : m)));

      try {
        const doFetch = (token, cid) =>
          fetch(COMPLETIONS, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-algolia-application-id": APP_ID,
              "x-algolia-api-key": SEARCH_KEY,
              ...(token ? { "X-Algolia-Secure-User-Token": token } : {}),
            },
            body: JSON.stringify({
              ...(cid ? { id: cid } : {}),
              messages: history.map((m) => ({
                role: m.role,
                parts: [{ type: "text", text: m.text }],
              })),
            }),
          });

        // Memory ON -> sign a token (recall binds to its sub); OFF -> no token, anonymous.
        const sut = _memoryEnabled ? await secureUserToken() : null;
        let res = await doFetch(sut, conversationId());
        // Safety net: never let a token hiccup hard-break the chat. If a token-bearing
        // request is rejected, retry once anonymously (memory degrades silently, the
        // visitor still gets an answer) on a fresh ephemeral thread to avoid id conflicts.
        if (!res.ok && sut) res = await doFetch(null, ephemeralId());
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        const toolNames = {}; // toolCallId -> toolName, to classify tool events
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const events = buf.split("\n\n");
          buf = events.pop() || "";
          for (const evt of events) {
            const line = evt.split("\n").find((l) => l.startsWith("data:"));
            if (!line) continue;
            const data = line.slice(5).trim();
            if (!data || data === "[DONE]") continue;
            let p;
            try { p = JSON.parse(data); } catch { continue; }
            if (p.type === "start" && p.messageId) {
              // server-assigned id, needed to vote on this message (#38)
              patch((m) => ({ ...m, algMsgId: p.messageId }));
            } else if (p.type === "text-delta" && p.delta) {
              patch((m) => ({ ...m, text: m.text + p.delta }));
            } else if (p.type === "tool-input-available" && p.toolName === ACTION_TOOL) {
              // client-side action tool (#22): the agent asks the browser to render a
              // click-to-act card. No server output follows (it ends the turn cleanly,
              // verified), so this IS the whole signal: capture the action, render it.
              const a = p.input?.action;
              if (a && ACTIONS[a]) patch((m) => ({ ...m, actions: [...(m.actions || []), a], acting: false }));
            } else if (p.type === "tool-output-available" || p.type === "tool-result") {
              const recs = recordsFromToolOutput(p.output ?? p.result);
              if (recs.length) patch((m) => ({ ...m, records: [...m.records, ...recs] }));
              patch((m) => ({ ...m, searching: false })); // tool finished
            } else if (p.type === "tool-output-error") {
              patch((m) => ({ ...m, searching: false }));
            } else if (typeof p.type === "string" && p.type.startsWith("tool-")) {
              // A tool is running: tool-input-start/-delta/-available, tool-call. Tell the
              // index SEARCH (forest "searching") from the client-side ACTION tool (bright
              // "acting") by toolName, tracked by toolCallId across its nameless deltas.
              if (p.toolName && p.toolCallId) toolNames[p.toolCallId] = p.toolName;
              const isAction = (p.toolName || toolNames[p.toolCallId]) === ACTION_TOOL;
              patch((m) => (isAction ? { ...m, acting: true } : { ...m, searching: true }));
            } else if (p.type === "data-suggestions" && p.data?.suggestions?.length) {
              patch((m) => ({ ...m, suggestions: p.data.suggestions.slice(0, 3) }));
            } else if (p.type === "error" && p.errorText) {
              patch((m) => ({ ...m, text: m.text || `Oups : ${p.errorText}`, error: true }));
            }
          }
        }
      } catch (e) {
        patch((m) => ({
          ...m,
          error: true,
          text:
            m.text ||
            "Oups, je n'arrive pas à joindre mes connaissances là tout de suite 🌧️. Réessaie dans un instant, ou va voir le Notion officiel.",
        }));
      } finally {
        setBusy(false);
      }
    },
    [messages, busy]
  );

  // Wipe the visible thread (used by "repartir à zéro" and "m'oublier"): a fresh
  // start so no prior context (possibly PII) rides along in the next completion.
  const reset = useCallback(() => { idRef.current = 0; setMessages([]); }, []);

  return { messages, busy, send, reset };
}

// Reads/persists the memory preference and keeps the module flag in sync, so the
// plain-function streaming client sees the current choice.
function useMemory() {
  const [enabled, setEnabled] = useState(() => {
    try { return window.localStorage.getItem("iris_memory") !== "off"; } catch { return true; }
  });
  useEffect(() => { _memoryEnabled = enabled; }, [enabled]);
  const set = useCallback((on) => { applyMemoryEnabled(on); setEnabled(on); }, []);
  return { enabled, set };
}

// Feedback reuses the composer: a thumb on a message (or the 💡 bubble) opens a
// target; Enter writes one record to opal_feedback; then we drop back to chat.
function useFeedback() {
  const [target, setTarget] = useState(null); // {kind, msgId?, question?, answer?}
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [rated, setRated] = useState(() => new Set());

  // Switching targets without submitting is the same as cancelling the prior one:
  // flush its bare native vote so a wordless thumb on message A still counts when
  // the visitor jumps to message B (mirrors cancel; guards same-target reopen).
  const open = useCallback((t) => {
    if (target?.algMsgId && status === "idle" && target.algMsgId !== t.algMsgId)
      voteFeedback(target.algMsgId, target.kind === "up" ? 1 : 0, { tags: [target.kind] });
    setStatus("idle");
    setTarget(t);
  }, [target, status]);
  // Closing a message-thumb without words is still a vote: fire the bare native
  // vote (kind as tag) so a wordless 👍/👎 counts. Idea-mode has no algMsgId, so
  // nothing fires. See voteFeedback (#41): the vote happens here, not on click.
  // The vote is a side effect, so it lives in the callback body (not a setTarget
  // updater, which StrictMode double-invokes), reading target from deps.
  const cancel = useCallback(() => {
    if (target?.algMsgId) voteFeedback(target.algMsgId, target.kind === "up" ? 1 : 0, { tags: [target.kind] });
    setTarget(null);
    setStatus("idle");
  }, [target]);
  const reset = useCallback(() => setStatus("idle"), []);

  const submit = useCallback(
    async (text) => {
      if (!target || status === "sending") return false;
      setStatus("sending");
      try {
        await submitFeedback({
          type: target.kind,
          text: text.trim() || undefined,
          question: target.question || undefined,
          answer: target.answer || undefined,
        });
        // Enrich the native per-message vote with the written correction (#41):
        // the dashboard now sees the words, not just the thumb. Best-effort.
        if (target.algMsgId)
          voteFeedback(target.algMsgId, target.kind === "up" ? 1 : 0, {
            notes: text,
            tags: [target.kind],
          });
        if (target.msgId != null) setRated((s) => new Set(s).add(target.msgId));
        setTarget(null);
        setStatus("sent");
        return true;
      } catch {
        setStatus("error"); // keep target + the user's words so they can retry
        return false;
      }
    },
    [target, status]
  );

  return { target, status, rated, open, cancel, reset, submit };
}

// state drives the aura's mood: idle (rainbow, slow), thinking (cool, quick pulse),
// responding (warm, bright), searching (forest-green, fast: a search tool call), acting
// (bright amber, eager: a client-side action tool call), memory (violet, gentle pulse:
// a save/recall moment), error (desaturated, still). See iris.css.
const AVATAR_STATE_LABEL = {
  thinking: "Iris réfléchit",
  responding: "Iris répond",
  searching: "Iris cherche dans la forêt",
  acting: "Iris te propose une action",
  memory: "Iris fait appel à sa mémoire",
  error: "Iris a rencontré un souci",
};
function IrisAvatar({ size, state = "idle", decorative = false }) {
  const sz = size === "lg" ? " iris-avatar--lg" : size === "md" ? " iris-avatar--md"
    : size === "sm" ? " iris-avatar--sm" : "";
  const st = state !== "idle" ? ` iris-avatar--${state}` : "";
  // decorative: when an adjacent aria-live region already voices the state (the
  // dock), hide the orb from SRs so the change isn't announced twice.
  return <div className={`iris-avatar${sz}${st}`}
              role={decorative ? undefined : "img"}
              aria-hidden={decorative || undefined}
              aria-label={decorative ? undefined : (AVATAR_STATE_LABEL[state] || "Iris")} />;
}

// Iris's live state as words, beside the orb in the dock (#39). aria-live
// announces the change to screen readers; idle is silent (empty string).
const ORB_STATUS = {
  idle: "",
  thinking: "Iris réfléchit…",
  responding: "Iris répond…",
  searching: "Iris cherche dans la forêt… 🔍",
  acting: "Iris te prépare un coup de main… ✨",
  memory: "Iris fait appel à sa mémoire… 🧠",
  error: "Iris a rencontré un souci 🌧️",
};

// Iris verbalizes memory in her prose (the only honest in-stream signal: the API emits
// no memory tool events, see IRIS/docs/algolia.md §14). We detect that phrasing to mark
// the moment (orb mood + a per-message tag). RECALL wins over SAVE when both appear.
const MEM_RECALL_RE = /je me souviens|je me rappelle|tu m'avais dit|la dernière fois|je n'ai pas oublié|comme tu (m'as|me l'as) dit/i;
const MEM_SAVE_RE = /je note|c'est noté|je garde (ça|ceci|bien|en (tête|mémoire))|je retiens|je m'en souviendrai|je le garde/i;
function memoryTag(text) {
  const t = text || "";
  if (MEM_RECALL_RE.test(t)) return "je me souviens de toi";
  if (MEM_SAVE_RE.test(t)) return "mémorisé";
  return null;
}

// Client-side action tool (#22): the agent calls suggest_action({action}) and the
// browser renders the matching card. The agent only ever picks an action KEY from an
// enum; every URL lives HERE, frontend-side, so Iris can't fabricate a broken link
// (the recurring failure mode, see no-url-fabrication). To add an action: define it
// here AND add the enum value in agent/client_tools.py. Keep the two in lock-step.
const ACTION_TOOL = "suggest_action";
const ACTIONS = {
  covoit: {
    emoji: "🚗",
    title: "Covoiturage Opal",
    blurb: "Trouve une place ou propose la tienne. On partage les frais, et on fait connaissance avant même la forêt.",
    ctas: [
      { emoji: "🔍", label: "Cherche une place", href: "https://togetzer.com/index.php?view=chercherVoiture&idtf=74419" },
      { emoji: "🚙", label: "Propose ton coffre", href: "https://togetzer.com/index.php?view=proposerVoiture&idtf=74419" },
    ],
    more: { label: "Voir tout le covoiturage", href: "https://www.opal-festival.com/covoit" },
  },
};

function ActionCard({ action }) {
  const a = ACTIONS[action];
  if (!a) return null;
  return (
    <article className="iris-action">
      <header className="iris-action__head">
        <span className="iris-action__emoji" aria-hidden="true">{a.emoji}</span>
        <div className="iris-action__meta">
          <h3 className="iris-action__title">{a.title}</h3>
          {a.blurb && <p className="iris-action__blurb">{a.blurb}</p>}
        </div>
      </header>
      <div className="iris-action__ctas">
        {a.ctas.map((c) => (
          <a key={c.href} className="iris-action__btn" href={c.href} target="_blank" rel="noreferrer">
            <span aria-hidden="true">{c.emoji}</span> {c.label}
          </a>
        ))}
      </div>
      {a.more && (
        <a className="iris-action__more" href={a.more.href} target="_blank" rel="noreferrer">
          {a.more.label} ↗
        </a>
      )}
    </article>
  );
}

function RecordCard({ rec }) {
  const badge = BADGES[rec.type] || { emoji: "✨", label: rec.type || "Opal", color: "var(--accent)" };
  const accent = rec.color || badge.color;
  return (
    <article className="iris-card" style={{ "--accent-c": accent }}>
      <header className="iris-card__head">
        <span className="iris-badge" style={{ background: accent }}>
          {badge.emoji} {badge.label}
        </span>
        <h3 className="iris-card__title">{rec.title}</h3>
      </header>
      {rec.content && (
        <p className="iris-card__body">
          {rec.content.length > 220 ? rec.content.slice(0, 220).trimEnd() + "…" : rec.content}
        </p>
      )}
      <footer className="iris-card__foot">
        {rec.join_url && (
          <a className="iris-card__cta" href={rec.join_url} target="_blank" rel="noreferrer">
            Rejoindre →
          </a>
        )}
        {rec.url && (
          <a className="iris-card__src" href={rec.url} target="_blank" rel="noreferrer">
            {rec.source || "source"} ↗
          </a>
        )}
      </footer>
    </article>
  );
}

function Message({ msg, busy, isLast, onSend, onRate, rated, memoryOn }) {
  if (msg.role === "user") {
    return (
      <div className="iris-msg iris-msg--user">
        <div className="iris-msg__body" style={{ whiteSpace: "pre-wrap" }}>{msg.text}</div>
      </div>
    );
  }
  const thinking = busy && isLast && !msg.text;
  const done = msg.text && !(busy && isLast); // answer fully streamed: ratable
  const showFollowups = isLast && !busy && msg.suggestions?.length > 0;
  return (
    <div className="iris-msg iris-msg--iris">
      <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
        <div className="iris-msg__body">
          {thinking ? (
            <span className="iris-thinking" aria-label="Iris réfléchit">
              <span /><span /><span />
            </span>
          ) : (
            <Rich text={msg.text} />
          )}
        </div>
        {!thinking && (() => {
          const media = mediaIn(msg.text);
          return media.length > 0 && (
            <div className="iris-medias">
              {media.map((d, i) => <MediaCard key={d.url + i} data={d} />)}
            </div>
          );
        })()}
        {!thinking && (() => {
          const lps = previewsIn(msg.text);
          return lps.length > 0 && (
            <div className="iris-lps">
              {lps.map((d, i) => <LinkPreview key={d.url + i} data={d} />)}
            </div>
          );
        })()}
        {!thinking && msg.actions?.length > 0 && (
          <div className="iris-actions">
            {msg.actions.map((a, i) => <ActionCard key={a + i} action={a} />)}
          </div>
        )}
        {SHOW_RECORDS && msg.records.length > 0 && (
          <div className="iris-records">
            {msg.records.map((r, i) => <RecordCard key={i} rec={r} />)}
          </div>
        )}
        {done && memoryOn && memoryTag(msg.text) && (
          <div className="iris-memtag" title="Iris s'appuie sur sa mémoire (tu la gères via le 🧠)">
            <span aria-hidden="true">🧠</span> {memoryTag(msg.text)}
          </div>
        )}
        {done && (
          rated ? (
            <div className="iris-rate iris-rate--done">merci 🌿</div>
          ) : (
            <div className="iris-rate">
              <button className="iris-rate__btn" onClick={() => onRate(msg, "up")}
                aria-label="Bonne réponse" title="C'est top">👍</button>
              <button className="iris-rate__btn" onClick={() => onRate(msg, "down")}
                aria-label="Réponse à corriger" title="Corrige-moi">👎</button>
            </div>
          )
        )}
        {isLast && done && (
          <div className="iris-ctas">
            {exitCtas(msg.text).map((c) => (
              <a key={c.href} className="iris-cta" href={c.href}
                 target="_blank" rel="noreferrer">
                <span aria-hidden="true">{c.emoji}</span> {c.label} ↗
              </a>
            ))}
          </div>
        )}
        {showFollowups && (
          <div className="iris-followups">
            {msg.suggestions.map((s) => (
              <button key={s} className="iris-chip iris-chip--sm" onClick={() => onSend(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Composer({ onSend, busy, fb, onIdea, orbState = "idle", showOrb, rootRef }) {
  const [val, setVal] = useState("");
  const taRef = useRef(null);
  const inFeedback = !!fb.target;
  const ui = inFeedback ? FEEDBACK_UI[fb.target.kind] : null;

  const grow = (el) => {
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  };
  const clear = () => {
    setVal("");
    if (taRef.current) taRef.current.style.height = "auto";
  };

  // --- voice input: Web Speech API, only when the device/browser offers it ----
  // Feature-detected, so the mic button never shows where it can't work (most
  // desktop Firefox, older browsers). Dictation appends to the field; the visitor
  // reviews and sends. We never auto-send what the recognizer heard.
  const SpeechRec = typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  const voiceSupported = !!SpeechRec;
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const voiceBaseRef = useRef("");   // text already typed when dictation started

  const stopVoice = useCallback(() => {
    if (recRef.current) { try { recRef.current.stop(); } catch { /* already stopped */ } }
  }, []);

  const startVoice = useCallback(() => {
    if (!voiceSupported || recRef.current) return;
    const rec = new SpeechRec();
    rec.lang = "fr-FR";
    rec.interimResults = true;
    rec.continuous = false;
    voiceBaseRef.current = val ? val.trimEnd() + " " : "";
    rec.onresult = (e) => {
      let heard = "";
      for (let i = 0; i < e.results.length; i++) heard += e.results[i][0].transcript;
      setVal((voiceBaseRef.current + heard).slice(0, 2000));
      if (taRef.current) grow(taRef.current);
    };
    rec.onerror = () => setListening(false);   // e.g. permission denied / no-speech
    rec.onend = () => {
      setListening(false);
      recRef.current = null;
      if (taRef.current) taRef.current.focus();
    };
    recRef.current = rec;
    setListening(true);
    try { rec.start(); } catch { setListening(false); recRef.current = null; }
  }, [voiceSupported, SpeechRec, val]);

  // Stop any live recognition if the composer unmounts.
  useEffect(() => () => { if (recRef.current) { try { recRef.current.stop(); } catch { /* noop */ } } }, []);

  // Autofocus the input on load, DESKTOP ONLY: on touch devices focusing would pop the
  // on-screen keyboard and shove the welcome screen offscreen, so we gate on a fine
  // pointer with hover (a mouse). Runs once on mount.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    if (window.matchMedia("(hover: hover) and (pointer: fine)").matches && taRef.current)
      taRef.current.focus();
  }, []);

  // Entering feedback mode: focus the (reused) input so Enter sends the feedback.
  useEffect(() => {
    if (inFeedback && taRef.current) taRef.current.focus();
  }, [inFeedback, fb.target]);

  const submit = async () => {
    if (inFeedback) {
      if (fb.status === "sending") return;
      if (!ui.optional && !val.trim()) return;
      const ok = await fb.submit(val);
      if (ok) clear(); // on error keep their words so they can retry
      return;
    }
    if (!val.trim() || busy) return;
    onSend(val);
    clear();
  };

  const cancelFb = () => { fb.cancel(); clear(); };

  const sendDisabled = inFeedback
    ? fb.status === "sending" || (!ui.optional && !val.trim())
    : !val.trim() || busy;

  return (
    <div className="iris-composer" ref={rootRef}>
      {showOrb && (
        <div className="iris-dock">
          <IrisAvatar size="md" state={orbState} decorative />
          <span className="iris-dock__status" aria-live="polite">{ORB_STATUS[orbState]}</span>
        </div>
      )}
      {inFeedback && (
        <div className="iris-fb" role="region" aria-label="Feedback pour Iris">
          <span className="iris-fb__icon" aria-hidden="true">{ui.icon}</span>
          <span className="iris-fb__title">{ui.title}</span>
          <button className="iris-fb__x" onClick={cancelFb} aria-label="Annuler le feedback">✕</button>
        </div>
      )}
      <div className="iris-composer__inner">
        <textarea
          ref={taRef}
          rows={1}
          value={val}
          placeholder={ui ? ui.placeholder : "Demande à Iris…  (packing, horaires, guildes, valeurs)"}
          onChange={(e) => { setVal(e.target.value); grow(e.target); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
            if (e.key === "Escape" && inFeedback) cancelFb();
          }}
          aria-label={inFeedback ? "Ton feedback pour Iris" : "Ton message pour Iris"}
        />
        {voiceSupported && (
          <button
            type="button"
            className={"iris-mic" + (listening ? " iris-mic--on" : "")}
            onClick={listening ? stopVoice : startVoice}
            aria-label={listening ? "Arrêter la dictée vocale" : "Dicter à la voix"}
            aria-pressed={listening}
            title={listening ? "Arrêter" : "Parler"}>
            {listening ? "■" : "🎤"}
          </button>
        )}
        <button className="iris-send" onClick={submit} disabled={sendDisabled}
          aria-label={inFeedback ? "Envoyer le feedback" : "Envoyer"}>
          {fb.status === "sending" ? "…" : "↑"}
        </button>
      </div>
      {fb.status === "error" && (
        <p className="iris-fb__msg iris-fb__msg--err">Je n'ai pas pu enregistrer ça 🌧️. Réessaie dans un instant.</p>
      )}
      {fb.status === "sent" && (
        <p className="iris-fb__msg iris-fb__msg--ok">Merci 🌿 c'est noté pour l'équipe.</p>
      )}
      <nav className="iris-footer" aria-label="Liens Opal">
        {FOOTER_LINKS.map((l) => (
          <a key={l.label} href={l.href} target="_blank" rel="noreferrer">{l.label}</a>
        ))}
        <button type="button" className="iris-footer__idea" onClick={onIdea}>💡 Une idée ?</button>
      </nav>
      <p className="iris-disclaimer">
        Iris peut se tromper, vérifie sur le{" "}
        <a href={NOTION_URL} target="_blank" rel="noreferrer">Notion officiel</a> ✨
      </p>
    </div>
  );
}

// Memory controls (#57): a header popover. On by default and visible; turning it off
// with a live thread warns (the prose so far may hold PII) and offers a clean restart
// vs continue-muted; "m'oublier" rotates the identity (the only real forget); "que
// sais-tu de moi" fires a meta-query (the only honest way to surface what's stored).
function MemoryControl({ enabled, onSetEnabled, hasThread, onForget, onAskMemories, onResetThread }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // null | "off" | "forget"
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const dismiss = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      if (e.type === "mousedown" && rootRef.current && rootRef.current.contains(e.target)) return;
      setOpen(false); setConfirm(null);
    };
    document.addEventListener("mousedown", dismiss);
    document.addEventListener("keydown", dismiss);
    return () => {
      document.removeEventListener("mousedown", dismiss);
      document.removeEventListener("keydown", dismiss);
    };
  }, [open]);

  const close = () => { setOpen(false); setConfirm(null); };
  const toggle = () => {
    if (!enabled) { onSetEnabled(true); return; }     // turning back on is harmless
    if (hasThread) setConfirm("off");                 // off mid-thread: warn about PII
    else onSetEnabled(false);
  };

  return (
    <div className="iris-mem" ref={rootRef}>
      <button type="button"
        className={"iris-mem__btn" + (enabled ? " iris-mem__btn--on" : "")}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open} aria-haspopup="dialog"
        aria-label="Réglages de ma mémoire" title="Ma mémoire">
        🧠
      </button>
      {open && (
        <div className="iris-mem__pop" role="dialog" aria-label="Ma mémoire">
          <div className="iris-mem__row">
            <div className="iris-mem__head">
              <span className="iris-mem__title">Ma mémoire</span>
              <span className="iris-mem__hint">
                {enabled
                  ? "Je me souviens de toi d'une visite à l'autre 🌱"
                  : "Je te rencontre à neuf à chaque fois 🌫️"}
              </span>
            </div>
            <button type="button" role="switch" aria-checked={enabled}
              className={"iris-switch" + (enabled ? " iris-switch--on" : "")}
              onClick={toggle}
              aria-label={enabled ? "Couper ma mémoire" : "Activer ma mémoire"}>
              <span className="iris-switch__knob" aria-hidden="true" />
            </button>
          </div>

          {confirm === "off" && (
            <div className="iris-mem__warn" role="group" aria-label="Couper la mémoire">
              <p>
                Si je coupe ma mémoire, je te traite comme une inconnue. Et ce qu'on s'est
                déjà dit ici peut contenir des infos perso 🌧️. On fait comment ?
              </p>
              <div className="iris-mem__acts">
                <button type="button" className="iris-mem__act"
                  onClick={() => { onResetThread(); onSetEnabled(false); close(); }}>
                  Repartir à zéro, sans mémoire
                </button>
                <button type="button" className="iris-mem__act"
                  onClick={() => { onSetEnabled(false); setConfirm(null); }}>
                  Continuer ici, mémoire coupée
                </button>
              </div>
              <button type="button" className="iris-mem__cancel" onClick={() => setConfirm(null)}>
                Annuler, garde ta mémoire
              </button>
            </div>
          )}

          {confirm === "forget" && (
            <div className="iris-mem__warn" role="group" aria-label="Tout effacer">
              <p>
                J'oublie tout ce que je sais de toi, et on se rencontre pour la première
                fois. C'est sans retour 🍃. Sûr·e ?
              </p>
              <div className="iris-mem__acts">
                <button type="button" className="iris-mem__act iris-mem__act--danger"
                  onClick={() => { onForget(); close(); }}>
                  Oui, oublie-moi
                </button>
                <button type="button" className="iris-mem__cancel" onClick={() => setConfirm(null)}>
                  Non, garde nos souvenirs
                </button>
              </div>
            </div>
          )}

          {!confirm && (
            <div className="iris-mem__actions">
              {enabled && (
                <button type="button" className="iris-mem__link"
                  onClick={() => { onAskMemories(); close(); }}>
                  🪞 Que sais-tu de moi ?
                </button>
              )}
              <button type="button" className="iris-mem__link iris-mem__link--danger"
                onClick={() => setConfirm("forget")}>
                🍃 M'oublier complètement
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IrisHeader({ theme, onToggleTheme, memory }) {
  const dark = theme === "dark";
  return (
    <header className="iris-top">
      {STANDALONE ? (
        <a href="https://opal-festival.com" className="iris-back">← opal-festival.com</a>
      ) : (
        <Link to="/" className="iris-back">← Les Guildes</Link>
      )}
      <div className="iris-brand">
        <div>
          <h1 className="iris-title">Iris</h1>
          <p className="iris-subtitle">messagère arc-en-ciel · Danse de la forêt 2026</p>
        </div>
      </div>
      {memory && <MemoryControl {...memory} />}
      {onToggleTheme && (
        <button type="button" className="iris-theme-toggle" onClick={onToggleTheme}
          aria-label={dark ? "Passer en mode clair" : "Passer en mode sombre"}
          title={dark ? "Mode clair" : "Mode sombre"}>
          {dark ? "☀️" : "🌙"}
        </button>
      )}
    </header>
  );
}

function SetupNotice() {
  return (
    <div className="iris-setup">
      <h2>🌈 Presque prête…</h2>
      <p>Iris a besoin de sa clé de recherche. Ajoute dans <code>.env.local</code> :</p>
      <pre>{`REACT_APP_ALGOLIA_SEARCH_KEY=<Search-Only API Key>`}</pre>
      <p>(Dashboard Algolia → Settings → API Keys, appli <code>{APP_ID}</code>), puis relance <code>pnpm start</code>.</p>
    </div>
  );
}

export default function IrisDemo() {
  const { messages, busy, send, reset } = useIris();
  const fb = useFeedback();
  const memory = useMemory();
  const { resolved: theme, toggle: toggleTheme } = useTheme();
  const endRef = useRef(null);
  const composerRef = useRef(null);

  // The composer is sticky and overlays the thread bottom. Measure its real height
  // (it grows with the dock, the feedback banner, a wrapped footer) and reserve that
  // much padding under the thread, so the last message always clears it, never
  // hidden behind the input bar. ResizeObserver catches every height change.
  const [composerH, setComposerH] = useState(0);
  useEffect(() => {
    const el = composerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setComposerH(el.offsetHeight));
    ro.observe(el);
    setComposerH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, composerH]);

  // Clear the "merci" confirmation after a beat.
  useEffect(() => {
    if (fb.status !== "sent") return;
    const t = setTimeout(fb.reset, 2600);
    return () => clearTimeout(t);
  }, [fb.status, fb.reset]);

  // Asking a question leaves feedback mode (chips/composer share the same input).
  const ask = useCallback((t) => { fb.cancel(); send(t); }, [fb, send]);

  // "Que sais-tu de moi ?": the only honest readout the API allows is to ask Iris
  // herself; her answer streams as a normal bubble (no memory metadata in the stream).
  const askMemories = useCallback(
    () => ask("Qu'est-ce que tu te souviens de moi ? Fais-moi la liste de ce que tu sais sur moi, avec tes mots."),
    [ask]
  );
  // Forget: rotate the identity (new sub) then wipe the thread (nothing old is recalled).
  const onForget = useCallback(() => { rotateIris(); reset(); }, [reset]);
  const memoryProps = {
    enabled: memory.enabled,
    onSetEnabled: memory.set,
    hasThread: messages.length > 0,
    onForget,
    onAskMemories: askMemories,
    onResetThread: reset,
  };

  // A thumb opens feedback mode with the exchange as context (preceding question).
  // The native vote (#38) fires once at submit/cancel, carrying the written note
  // as `notes` (#41), so we pass algMsgId through instead of voting on click.
  const onRate = useCallback((msg, kind) => {
    const idx = messages.findIndex((m) => m.id === msg.id);
    let question = "";
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].role === "user") { question = messages[i].text; break; }
    }
    fb.open({ kind, msgId: msg.id, algMsgId: msg.algMsgId, question, answer: msg.text });
  }, [messages, fb]);

  if (!SEARCH_KEY) {
    return (
      <div className="iris-page" data-theme={theme}>
        <IrisHeader theme={theme} onToggleTheme={toggleTheme} />
        <SetupNotice />
      </div>
    );
  }

  const empty = messages.length === 0;

  // Global orb mood, lifted out of per-message avatars: the centerpiece reflects
  // the conversation as a whole (#39). busy + no streamed text yet = thinking;
  // busy + text arriving = responding; a last error = error; otherwise calm.
  const last = messages[messages.length - 1];
  // a memory moment = memory on AND Iris verbalized a save/recall in the streaming text
  const memoryish = !!last && last.role === "assistant" && memory.enabled && memoryTag(last.text) !== null;
  const orbState = last?.error ? "error"
    : busy && last?.searching ? "searching"
    : busy && last?.acting ? "acting"
    : busy && memoryish && last?.text ? "memory"
    : busy && last?.role === "assistant" && last.text ? "responding"
    : busy ? "thinking"
    : "idle";

  return (
    <div className="iris-page" data-theme={theme}>
      <IrisHeader theme={theme} onToggleTheme={toggleTheme} memory={memoryProps} />
      <main className="iris-chat">
        {empty ? (
          <div className="iris-welcome">
            <IrisAvatar size="lg" />
            <h2>Coucou, moi c'est Iris ✨</h2>
            <p>
              Je relie les opalien·nes à l'info. Pose-moi tes questions sur Opal :
              le sac, les horaires, les guildes, l'esprit du festival.
            </p>
            <div className="iris-chips">
              {SUGGESTIONS.map((s) => (
                <button key={s} className="iris-chip" onClick={() => ask(s)} disabled={busy}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="iris-thread" style={{ paddingBottom: (composerH || 180) + 24 }}>
            {messages.map((m, i) => (
              <Message key={m.id} msg={m} busy={busy} isLast={i === messages.length - 1}
                onSend={ask} onRate={onRate} rated={fb.rated.has(m.id)} memoryOn={memory.enabled} />
            ))}
            <div ref={endRef} />
          </div>
        )}
      </main>
      <Composer onSend={ask} busy={busy} fb={fb} onIdea={() => fb.open({ kind: "idea" })}
        orbState={orbState} showOrb={!empty} rootRef={composerRef} />
    </div>
  );
}
